/* eslint-disable */
import {
    Box,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    InputAdornment,
    Paper,
    TableBody,
    TableCell,
    TableRow,
    TextField,
    Toolbar,
    Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DeleteForeverOutlined, Search } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/Edit';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import RefreshIcon from '@mui/icons-material/Refresh';
import React, { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { useDispatch, useSelector } from 'react-redux';
import { Controls } from '../../components/controls/Controls';
import PageHeader from '../../components/PageHeader';
import Popup from '../../components/Popup';
import useTable from '../../components/useTable';
import { loadControlIngresos, saveControlIngreso } from '../../redux/ducks/controlIngresos';
import { setSnackbar } from '../../redux/ducks/snackbar';
import ControlIngresosForm from './ControlIngresosForm';
import ConfirmDialog from '../../components/ConfirmDialog';
import http from '../../http-common';
import { filterOnAnyField } from '../../utils';

const useStyles = makeStyles((theme) => ({
    pageContent: {
        margin: theme.spacing(2),
        marginTop: theme.spacing(1),
        padding: theme.spacing(2),
    },
    searchInput: {
        width: '25%',
    },
}));

const MESES_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const formatFecha = (fecha) => {
    if (!fecha) return '';
    const date = new Date(typeof fecha === 'string' && !fecha.includes('T') ? `${fecha}T00:00:00` : fecha);
    if (Number.isNaN(date.getTime())) return fecha;
    return `${date.getDate()}-${MESES_ES[date.getMonth()]}-${date.getFullYear()}`;
};

const formatMoneda = (value) => {
    if (value == null) return '';
    return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2,
    }).format(value);
};

const headCells = [
    { id: 'aprobar', label: 'Aprobar', disableSorting: true },
    { id: 'id', label: 'ID' },
    { id: 'fecha', label: 'Fecha' },
    { id: 'cliente', label: 'Cliente' },
    { id: 'monto', label: 'Monto' },
    { id: 'banco', label: 'Banco' },
    { id: 'factura', label: 'Factura' },
    { id: 'folioComplementoPago', label: 'Folio Complemento' },
    { id: 'statusComplemento', label: 'Status Complemento' },
    { id: 'idMovimientosBancarios', label: 'ID Mov. Bancario' },
    { id: 'idCuentaBancaria', label: 'ID Cuenta Bancaria' },
    { id: 'comentarios', label: 'Comentarios' },
    { id: 'actions', label: 'Acciones', disableSorting: true },
];

export default function ControlIngresos() {
    const [openPopup, setOpenPopup] = useState(false);
    const classes = useStyles();
    const [filterFn, setFilterFn] = useState({ fn: (items) => items });
    const [recordForEdit, setRecordForEdit] = useState();
    const [filter, setFilter] = useState('');
    const [filterId, setFilterId] = useState('');
    const [filterIdMovBancario, setFilterIdMovBancario] = useState('');
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [approveDialogOpen, setApproveDialogOpen] = useState(false);
    const [approvePassword, setApprovePassword] = useState('');
    const [filterFechaInicio, setFilterFechaInicio] = useState('');
    const [filterFechaFin, setFilterFechaFin] = useState('');
    const [appliedFechaInicio, setAppliedFechaInicio] = useState('');
    const [appliedFechaFin, setAppliedFechaFin] = useState('');
    const [dateFilterApplied, setDateFilterApplied] = useState(false);
    const [soloPendientes, setSoloPendientes] = useState(false);

    const dispatch = useDispatch();
    const ingresos = useSelector((state) => state.controlIngresos.ingresos);
    const [ingresosLoading, setIngresosLoading] = useState(false);

    const ingresosFiltradosPorFecha = useMemo(() => {
        const data = ingresos || [];
        if (!dateFilterApplied || !appliedFechaInicio) return data;
        return data.filter((item) => {
            if (!item.fecha) return false;
            const match = String(item.fecha).match(/(\d{4}-\d{2}-\d{2})/);
            if (!match) return false;
            const fechaStr = match[1];
            if (appliedFechaFin) return fechaStr >= appliedFechaInicio && fechaStr <= appliedFechaFin;
            return fechaStr === appliedFechaInicio;
        });
    }, [ingresos, dateFilterApplied, appliedFechaInicio, appliedFechaFin]);

    const {
        TblContainer,
        TblHead,
        TblPagination,
        recordsAfterPagingAndSorting,
    } = useTable(ingresosFiltradosPorFecha, headCells, filterFn, 100, 'id', 'desc');

    useEffect(() => {
        setIngresosLoading(false);
    }, [ingresos]);

    const cargarIngresos = () => {
        setIngresosLoading(true);
        dispatch(loadControlIngresos());
    };

    useEffect(() => {
        cargarIngresos();
    }, [dispatch]);

    const resetDateFilter = () => {
        setSoloPendientes(false);
        setFilterFechaInicio('');
        setFilterFechaFin('');
        setAppliedFechaInicio('');
        setAppliedFechaFin('');
        setDateFilterApplied(false);
    };

    const handleSearch = (e) => {
        const { target } = e;
        setFilter(target.value);
        setFilterId('');
        setFilterIdMovBancario('');
        resetDateFilter();
        setFilterFn({
            fn: (items) => {
                if (target.value === '') return items;
                return filterOnAnyField(items, target.value.toLowerCase());
            },
        });
    };

    const addOrEdit = async (ingreso, resetForm) => {
        const isNew = !ingreso.id || ingreso.id === 0;
        try {
            if (isNew) {
                await http.post('/control-ingresos', ingreso);
            } else {
                await http.put(`/control-ingresos/${ingreso.id}`, ingreso);
            }
            dispatch(setSnackbar(true, 'success', isNew ? 'El registro se agregó correctamente' : 'El registro se actualizó correctamente'));
        } catch {
            dispatch(setSnackbar(true, 'error', isNew ? 'Error al agregar el registro' : 'Error al actualizar el registro'));
            return;
        }

        const clienteCambio = (ingreso.cliente || '') !== (recordForEdit?.cliente || '');
        const facturaCambio = (ingreso.factura || '') !== (recordForEdit?.factura || '');
        if (!isNew && ingreso.idMovimientosBancarios && (clienteCambio || facturaCambio)) {
            const sincronizado = await sincronizarMovimientoBancario(ingreso);
            if (!sincronizado) {
                dispatch(setSnackbar(true, 'warning', 'El ingreso se guardó pero no se pudo actualizar el Movimiento Bancario'));
            }
        }

        setOpenPopup(false);
        resetForm();
        setTimeout(() => cargarIngresos(), 500);
    };

    const sincronizarMovimientoBancario = async (ingreso) => {
        const idCuenta = ingreso.idCuentaBancaria;
        if (!idCuenta) return false;
        try {
            const movResp = await http.get(
                `/movimientos-bancarios/${ingreso.idMovimientosBancarios}/${idCuenta}`,
                { validateStatus: () => true },
            );
            if (movResp.status !== 200 || !movResp.data?.id) return false;
            // banco y cuentaBancaria son objetos anidados de solo lectura; no se reenvían al guardar
            const { banco, cuentaBancaria, ...movimiento } = movResp.data;
            const idBanco = movimiento.idBanco ?? banco?.id;
            if (!idBanco) return false;
            const saveResp = await http.post('/movimientos-bancarios', {
                ...movimiento,
                factura: ingreso.factura || null,
                idBanco,
                idCuenta,
                referencia: ingreso.cliente || null,
            }, { validateStatus: () => true });
            return saveResp.status >= 200 && saveResp.status < 300;
        } catch {
            return false;
        }
    };

    const openInPopup = (item) => {
        setRecordForEdit(item);
        setOpenPopup(true);
    };

    const toggleSeleccionado = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleAprobar = async () => {
        const payload = Array.from(selectedIds).map((id) => ({
            id,
            status: 'APROBADO',
            password: approvePassword,
        }));
        let resp;
        try {
            resp = await http.post('/control-ingresos/aprobarIngreso', payload, { validateStatus: () => true });
        } catch {
            resp = null;
        }
        if (!resp || resp.status < 200 || resp.status >= 300) {
            const msg = typeof resp?.data === 'string' ? resp.data : resp?.data?.message;
            dispatch(setSnackbar(true, 'error', msg || 'Error al aprobar el ingreso'));
            return;
        }
        setApproveDialogOpen(false);
        setApprovePassword('');
        setSelectedIds(new Set());
        cargarIngresos();
        dispatch(setSnackbar(true, 'success', 'El ingreso fue aprobado'));
    };

    const [confirmDialog, setConfirmDialog] = useState({
        title: 'title',
        subtitle: 'subtitle',
        isOpen: false,
    });

    const deleteIngreso = (item) => {
        setConfirmDialog({
            subtitle: '',
            title: '¿Desea borrar el ingreso?',
            isOpen: true,
            onConfirm: async () => {
                const resp = await http.delete(
                    `/control-ingresos/${item.id}`,
                    { validateStatus: () => true },
                );
                if (resp.status >= 400) {
                    const mensaje = resp.data?.message || resp.data || 'Error al borrar el registro';
                    dispatch(setSnackbar(true, 'error', mensaje));
                } else {
                    cargarIngresos();
                }
                setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
            },
        });
    };

    const exportToExcel = () => {
        const datos = filterFn.fn(ingresosFiltradosPorFecha);
        const filas = datos.map((item) => ({
            ID: item.id ?? '',
            Fecha: item.fecha ?? '',
            Cliente: item.cliente ?? '',
            Monto: item.monto ?? '',
            Banco: item.banco ?? '',
            Factura: item.factura ?? '',
            'Folio Complemento': item.folioComplementoPago ?? '',
            'Status Complemento': item.statusComplemento ?? '',
            Status: item.status ?? '',
            'ID Mov. Bancario': item.idMovimientosBancarios ?? '',
            Comentarios: item.comentarios ?? '',
        }));
        const worksheet = XLSX.utils.json_to_sheet(filas);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Control Ingresos');
        XLSX.writeFile(workbook, 'control_ingresos.xlsx');
    };

    return (
        <>
            <PageHeader
                icon={<AccountBalanceIcon fontSize='large' />}
                subtitle=''
                title='Control Ingresos'
            />
            <Paper className={classes.pageContent}>
                <Toolbar>
                    <Controls.Input
                        className={classes.searchInput}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position='end'>
                                    <Search />
                                </InputAdornment>
                            ),
                        }}
                        label='Buscar'
                        onChange={handleSearch}
                        value={filter}
                    />
                    <Controls.Button
                        onClick={() => cargarIngresos()}
                        startIcon={<RefreshIcon />}
                        text='Refrescar'
                        variant='outlined'
                    />
                    <Box sx={{ '& .MuiButton-root': { height: 36.5, whiteSpace: 'nowrap' } }}>
                        <Controls.Button
                            onClick={() => {
                                setRecordForEdit(null);
                                setOpenPopup(true);
                            }}
                            startIcon={<AddIcon />}
                            text='Agregar Nuevo'
                            variant='outlined'
                        />
                    </Box>
                    <Box sx={{ '& .MuiButton-root': { height: 36.5, whiteSpace: 'nowrap' } }}>
                        <Controls.Button
                            disabled={selectedIds.size === 0}
                            onClick={() => setApproveDialogOpen(true)}
                            startIcon={<CheckCircleOutlineIcon />}
                            sx={{
                                backgroundColor: selectedIds.size === 0 ? undefined : '#1976d2',
                                color: selectedIds.size === 0 ? undefined : 'white',
                                '&:hover': { backgroundColor: '#1565c0' },
                            }}
                            text='Aprobar Ingreso'
                        />
                    </Box>
                    <Controls.Input
                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                        label='Filtrar por ID'
                        onChange={(e) => {
                            const val = e.target.value;
                            if (val === '' || /^\d+$/.test(val)) setFilterId(val);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                setFilter('');
                                setFilterIdMovBancario('');
                                resetDateFilter();
                                if (filterId === '') {
                                    setFilterFn({ fn: (items) => items });
                                } else {
                                    setFilterFn({
                                        fn: (items) => items.filter(
                                            (item) => String(item.id) === filterId,
                                        ),
                                    });
                                }
                            }
                        }}
                        sx={{
                            ml: 4,
                            width: 160,
                            '& .MuiOutlinedInput-root': { height: 36.5 },
                            '& .MuiInputLabel-root': { top: -8 },
                            '& .MuiInputLabel-shrink': { top: 0 },
                        }}
                        value={filterId}
                    />
                    <Controls.Input
                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                        label='Filtrar por ID Mov. Bancario'
                        onChange={(e) => {
                            const val = e.target.value;
                            if (val === '' || /^\d+$/.test(val)) setFilterIdMovBancario(val);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                setFilter('');
                                setFilterId('');
                                resetDateFilter();
                                if (filterIdMovBancario === '') {
                                    setFilterFn({ fn: (items) => items });
                                } else {
                                    setFilterFn({
                                        fn: (items) => items.filter(
                                            (item) => String(item.idMovimientosBancarios) === filterIdMovBancario,
                                        ),
                                    });
                                }
                            }
                        }}
                        sx={{
                            width: 210,
                            '& .MuiOutlinedInput-root': { height: 36.5 },
                            '& .MuiInputLabel-root': { top: -8 },
                            '& .MuiInputLabel-shrink': { top: 0 },
                        }}
                        value={filterIdMovBancario}
                    />
                </Toolbar>

                {/* Fila de filtro por rango de fechas */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, pb: 1, mt: 2 }}>
                    <TextField
                        InputLabelProps={{ shrink: true }}
                        label='Fecha Inicio'
                        onChange={(e) => {
                            setFilterFechaInicio(e.target.value);
                            if (!e.target.value) {
                                setFilterFechaFin('');
                                setAppliedFechaInicio('');
                                setAppliedFechaFin('');
                                setDateFilterApplied(false);
                            }
                        }}
                        size='small'
                        sx={{ width: 180 }}
                        type='date'
                        value={filterFechaInicio}
                    />
                    <TextField
                        disabled={!filterFechaInicio}
                        InputLabelProps={{ shrink: true }}
                        label='Fecha Fin'
                        onChange={(e) => setFilterFechaFin(e.target.value)}
                        size='small'
                        sx={{ width: 180 }}
                        type='date'
                        value={filterFechaFin}
                    />
                    <Controls.Button
                        disabled={!filterFechaInicio}
                        onClick={() => {
                            setSoloPendientes(false);
                            setFilter('');
                            setFilterId('');
                            setFilterIdMovBancario('');
                            setFilterFn({ fn: (items) => items });
                            setAppliedFechaInicio(filterFechaInicio);
                            setAppliedFechaFin(filterFechaFin);
                            setDateFilterApplied(true);
                        }}
                        sx={{
                            height: 36.5,
                            whiteSpace: 'nowrap',
                            backgroundColor: '#1976d2',
                            color: 'white',
                            '&:hover': { backgroundColor: '#1565c0' },
                            '&:disabled': { backgroundColor: undefined, color: undefined },
                        }}
                        text='Buscar por fecha'
                    />
                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={soloPendientes}
                                onChange={(e) => {
                                    const marcado = e.target.checked;
                                    if (marcado) {
                                        setFilter('');
                                        setFilterId('');
                                        setFilterIdMovBancario('');
                                        resetDateFilter();
                                    }
                                    setSoloPendientes(marcado);
                                    setFilterFn(marcado
                                        ? { fn: (items) => items.filter((item) => item.status !== 'APROBADO') }
                                        : { fn: (items) => items });
                                }}
                                size='small'
                            />
                        )}
                        label='Pendientes por Aprobar'
                        sx={{ ml: 4, whiteSpace: 'nowrap' }}
                    />
                    <Controls.Button
                        onClick={exportToExcel}
                        sx={{
                            ml: 'auto',
                            height: 36.5,
                            whiteSpace: 'nowrap',
                            backgroundColor: '#2e7d32',
                            color: 'white',
                            '&:hover': { backgroundColor: '#1b5e20' },
                        }}
                        text='Exportar a Excel'
                    />
                </Box>

                <TblPagination />
                {ingresosLoading ? (
                    <Box sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        justifyContent: 'center',
                        minHeight: 300,
                        width: '100%',
                    }}
                    >
                        <CircularProgress size={64} sx={{ color: '#333996' }} thickness={4} />
                        <Typography sx={{ color: '#333996', fontWeight: 'bold' }}>
                            Cargando registros...
                        </Typography>
                    </Box>
                ) : (
                <Box sx={{
                    '& table': { borderCollapse: 'collapse' },
                    '& th': { border: '1px solid #9e9e9e', padding: '6px 10px', fontSize: '0.82rem', backgroundColor: '#e8eaf6' },
                    '& td': { border: '1px solid #c8c8c8', padding: '3px 10px', fontSize: '0.82rem' },
                }}>
                <TblContainer>
                    <TblHead />
                    <TableBody>
                        {recordsAfterPagingAndSorting().map((item) => (
                            <TableRow
                                key={item.id}
                                sx={{ backgroundColor: item.status === 'APROBADO' ? 'white' : '#fff9c4' }}
                            >
                                <TableCell padding='checkbox'>
                                    <Checkbox
                                        checked={selectedIds.has(item.id)}
                                        disabled={item.status === 'APROBADO'}
                                        onChange={() => toggleSeleccionado(item.id)}
                                    />
                                </TableCell>
                                <TableCell>{item.id}</TableCell>
                                <TableCell>{formatFecha(item.fecha)}</TableCell>
                                <TableCell>{item.cliente}</TableCell>
                                <TableCell align='right' sx={{ color: '#2e7d32', fontWeight: 500 }}>
                                    {formatMoneda(item.monto)}
                                </TableCell>
                                <TableCell>{item.banco}</TableCell>
                                <TableCell sx={{ width: 350 }}>
                                    {/* Máximo 350px; en pantallas chicas se angosta hasta 150px para que quepan las demás columnas */}
                                    <Box sx={{ maxWidth: 350, minWidth: 150, overflowWrap: 'anywhere', whiteSpace: 'normal' }}>
                                        {item.factura}
                                    </Box>
                                </TableCell>
                                <TableCell>{item.folioComplementoPago}</TableCell>
                                <TableCell>{item.statusComplemento}</TableCell>
                                <TableCell>{item.idMovimientosBancarios}</TableCell>
                                <TableCell>{item.idCuentaBancaria}</TableCell>
                                <TableCell>{item.comentarios}</TableCell>
                                <TableCell>
                                    <Controls.ActionButton
                                        color='primary'
                                        onClick={() => openInPopup(item)}
                                    >
                                        <EditOutlinedIcon fontSize='small' />
                                    </Controls.ActionButton>
                                    <Controls.ActionButton
                                        color='primary'
                                        onClick={() => deleteIngreso(item)}
                                    >
                                        <DeleteForeverOutlined fontSize='small' />
                                    </Controls.ActionButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </TblContainer>
                </Box>
                )}
                <TblPagination />
            </Paper>

            <Popup
                openPopup={openPopup}
                setOpenPopup={setOpenPopup}
                title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <span>Control Ingreso</span>
                        {recordForEdit?.idMovimientosBancarios && (
                            <Typography
                                sx={{
                                    fontSize: '0.78rem',
                                    color: '#e65100',
                                    backgroundColor: '#fff3e0',
                                    border: '1px solid #ffb74d',
                                    borderRadius: '4px',
                                    px: 1,
                                    py: 0.25,
                                    fontWeight: 500,
                                }}
                            >
                                Este registro está relacionado a Movimientos Bancarios, el
                                {' '}
                                <strong>MONTO</strong>
                                {' '}
                                se tiene que editar allá
                            </Typography>
                        )}
                    </Box>
                }
            >
                <ControlIngresosForm
                    addOrEdit={addOrEdit}
                    recordForEdit={recordForEdit}
                />
            </Popup>

            <ConfirmDialog
                confirmDialog={confirmDialog}
                setConfirmDialog={setConfirmDialog}
            />

            {/* Diálogo de aprobación con password */}
            <Dialog onClose={() => setApproveDialogOpen(false)} open={approveDialogOpen}>
                <DialogTitle>Aprobar ingresos</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }} variant='body2'>
                        {`Se aprobarán ${selectedIds.size} ingreso(s). Ingresa el password para continuar.`}
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        label='Password'
                        margin='dense'
                        onChange={(e) => setApprovePassword(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && approvePassword) handleAprobar(); }}
                        type='password'
                        value={approvePassword}
                    />
                </DialogContent>
                <DialogActions>
                    <Controls.Button
                        color='default'
                        onClick={() => { setApproveDialogOpen(false); setApprovePassword(''); }}
                        text='Cancelar'
                        variant='outlined'
                    />
                    <Controls.Button
                        disabled={!approvePassword}
                        onClick={handleAprobar}
                        startIcon={<CheckCircleOutlineIcon />}
                        text='Confirmar'
                    />
                </DialogActions>
            </Dialog>
        </>
    );
}
